import React from 'react';
import styled from 'styled-components';

import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     TYPES
// ============================================================
export interface ContentWrapperProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;

  /**
   * Determines how the sidebar is displayed.
   *
   * - `default` (default): A standard block element with sticky @ top: 0
   * - `primary`: Fixed to the left edge of the viewport. Should only be used once for the main navigation sidebar.
   *
   * @default default
   */
  variant?: 'default' | 'primary';

  /**
   * Custom widths for the sidebar open states
   */
  widths?: {
    /**
     * The width of the sidebar when collapsed
     *
     * NOTE: `rem` is recommended over `px` for accessibility consistency
     *
     * @default 5rem
     */
    collapsed?: string;
    /**
     * The width of the sidebar when expanded
     *
     * NOTE: `rem` is recommended over `px` for accessibility consistency
     *
     * @default 15rem
     */
    expanded?: string;
  };
}

// ============================================================
//     STYLED COMPONENTS
// ============================================================
const StyledWrapper = styled.div({
  position: 'sticky',
  top: 0,

  display: 'flex',
  flexDirection: 'column',
  flex: '0 0 auto',

  // FE2.0 declares both 100vh and 100dvh; styled-components object notation can't
  // hold duplicate keys, and dvh is supported by all of Daffy's target browsers
  maxHeight: '100dvh',
  width: 'var(--sidebar-width)',
  maxWidth: '100%',

  whiteSpace: 'nowrap',

  '&[data-expanded="false"]': {
    width: 'var(--sidebar-width-collapsed)',
  },

  '&[data-variant="primary"]': {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 'var(--cl-zindex-drawer)',
  },

  transitionProperty: 'width',
  transitionDuration: 'var(--cl-duration-400)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',

  '@media print': {
    display: 'none',
  },
});

const StyledFixedPlaceholder = styled.div({
  position: 'relative',
  flex: '0 0 auto',

  width: 'var(--sidebar-width)',
  maxWidth: '100%',

  '&[data-expanded="false"]': {
    width: 'var(--sidebar-width-collapsed)',
  },

  transitionProperty: 'width',
  transitionDuration: 'var(--cl-duration-400)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

// ============================================================
//     HELPERS
// ============================================================
const FixedVariantWrapper = (props: {
  children: React.ReactNode;
  isExpanded: boolean;
  isPrimary: boolean;
  widths: NonNullable<Required<ContentWrapperProps['widths']>>;
}) => {
  const {
    children, isExpanded, isPrimary, widths,
  } = props;

  if (!isPrimary) {
    return children as JSX.Element;
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 'var(--cl-zindex-drawer)',
    }}
    >
      <StyledFixedPlaceholder
        data-component="Sidebar.FixedPlaceholder"
        data-expanded={isExpanded}
        style={
          {
            '--sidebar-width': widths.expanded,
            '--sidebar-width-collapsed': widths.collapsed,
          } as React.CSSProperties
        }
      />
      {children}
    </div>
  );
};

// ============================================================
//     COMPONENT
// ============================================================
/**
 * Desktop wrapper for the sidebar content.
 *
 * Ported from the Frontend-Platform Sidebar wrapper, minus the mobile drawer branch.
 */
export const ContentWrapper = React.forwardRef<HTMLDivElement, ContentWrapperProps>(
  (props, forwardedRef) => {
    const {
      children, style, variant = 'default', widths, ...restProps
    } = props;

    const { isExpanded } = useSidebarCtx();

    const expandedWidth = widths?.expanded ?? '15rem'; // 240px
    const collapsedWidth = widths?.collapsed ?? '5rem'; // 80px

    const isPrimaryVariant = variant === 'primary';

    return (
      <FixedVariantWrapper
        isExpanded={isExpanded}
        isPrimary={isPrimaryVariant}
        widths={{ expanded: expandedWidth, collapsed: collapsedWidth }}
      >
        <StyledWrapper
          {...restProps}
          ref={forwardedRef}
          data-component="Sidebar.Wrapper"
          data-expanded={isExpanded}
          data-variant={variant}
          style={
            {
              '--sidebar-width': expandedWidth,
              '--sidebar-width-collapsed': collapsedWidth,
              ...style,
            } as React.CSSProperties
          }
        >
          {children}
        </StyledWrapper>
      </FixedVariantWrapper>
    );
  },
);

ContentWrapper.displayName = 'Sidebar.ContentWrapper' as const;
